import { useState } from "react";
import { cn } from "../../../app/components/primitives/utils";
import {
  ProductRender,
  type ProductRenderKind,
  type ProductRenderView,
} from "../../customer-pages/ProductRender";

const VIEWS: { view: ProductRenderView; label: string }[] = [
  { view: "front", label: "Front" },
  { view: "detail", label: "Close-up" },
  { view: "in-use", label: "On the desk" },
];

// A main image and three thumbnails. There is no photography, so the images
// are illustrations (and say so); they react to the chosen configuration
// where that is cheap -- black-only goes greyscale, gloss adds a sheen.
export function ProductGallery({
  kind,
  productName,
  monochrome,
  glossy,
  caption,
}: {
  kind: ProductRenderKind;
  productName: string;
  monochrome: boolean;
  glossy: boolean;
  /** The chosen options in words, e.g. "Full colour (CMYK) · 100gsm uncoated". */
  caption: string;
}) {
  const [activeView, setActiveView] = useState<ProductRenderView>("front");
  const active = VIEWS.find((v) => v.view === activeView) ?? VIEWS[0];

  return (
    <div className="space-y-3">
      <figure className="space-y-2">
        <div className="relative aspect-[4/3] overflow-hidden rounded-xl border bg-muted/40">
          <ProductRender
            kind={kind}
            view={active.view}
            monochrome={monochrome}
            glossy={glossy}
            title={`${productName}: ${active.label.toLowerCase()} view`}
          />
          <span className="absolute right-3 bottom-2 text-[11px] text-muted-foreground">
            Illustration
          </span>
        </div>
        {caption && (
          <figcaption className="text-center text-xs text-muted-foreground">
            {caption}
          </figcaption>
        )}
      </figure>
      <div className="grid grid-cols-3 gap-3">
        {VIEWS.map(({ view, label }) => (
          <button
            key={view}
            type="button"
            aria-label={`${label} view`}
            aria-pressed={view === activeView}
            onClick={() => setActiveView(view)}
            className={cn(
              "aspect-[4/3] overflow-hidden rounded-lg border bg-muted/40 outline-none transition-shadow",
              "hover:border-ring/60 focus-visible:ring-[3px] focus-visible:ring-ring/50",
              view === activeView && "border-primary ring-2 ring-primary/30",
            )}
          >
            <ProductRender
              kind={kind}
              view={view}
              monochrome={monochrome}
              glossy={glossy}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
